import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PaperAirplaneIcon,
  ChevronDownIcon,
  AdjustmentsHorizontalIcon,
  CpuChipIcon,
  UserIcon,
  PhotoIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  MusicalNoteIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { IProjectAsset, AspectRatioLabels, Resolution, ResolutionLabels, ModelType, IBgm, getLabelsByLocale, Locale } from '@packages/global';
import { useModelStore } from '../../../stores/modelStore';
import { CompactModelSelector } from '../../common/form';
import AttachmentCard from './AttachmentCard';
import StyleSelector from './StyleSelector';
import MusicSelector from '../../common/MusicSelector';
import type { MvSelectedModels } from '../hooks/useMvGeneration';

// ─── 子组件：上传区 ───────────────────────────────────────────────────────────

interface UploadAreaProps {
  audioFileName: string;
  uploading: boolean;
  disabled: boolean;
  onUploadClick: () => void;
  onClear: () => void;
  onSelectFromMusic: () => void;
  onSubmit: () => void;
  canSubmit: boolean;
  hasEnoughCoins: boolean;
  prompt: string;
  onPromptChange: (v: string) => void;
  selectedSubjects: IProjectAsset[];
  onOpenSubjectSelector: () => void;
  onRemoveSubject: (id: string) => void;
  selectedStyle: string;
  onStyleChange: (s: string) => void;
  aspectRatio: string;
  onAspectRatioChange: (v: string) => void;
  resolution: Resolution;
  onResolutionChange: (v: Resolution) => void;
  selectedModels: MvSelectedModels;
  onModelsChange: (models: MvSelectedModels) => void;
  maxShotDuration: number | null;
  onMaxShotDurationChange: (v: number | null) => void;
}

function UploadArea({
  audioFileName, uploading, disabled,
  onUploadClick, onClear, onSelectFromMusic,
  onSubmit, canSubmit, hasEnoughCoins,
  prompt, onPromptChange,
  selectedSubjects, onOpenSubjectSelector, onRemoveSubject,
  selectedStyle, onStyleChange,
  aspectRatio, onAspectRatioChange,
  resolution, onResolutionChange,
  selectedModels, onModelsChange,
  maxShotDuration, onMaxShotDurationChange,
}: UploadAreaProps) {
  const { getModelsByType } = useModelStore();
  const { t, i18n } = useTranslation(['script', 'common']);
  const aspectRatioLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);
  const resolutionLabels = getLabelsByLocale(ResolutionLabels, i18n.language as Locale);
  const llmModels = getModelsByType(ModelType.LLM);
  const llmVlModels = getModelsByType(ModelType.LLM_VL);
  const text2imageModels = getModelsByType(ModelType.TEXT_TO_IMAGE);
  const imageEditModels = getModelsByType(ModelType.IMAGE_EDIT);

  const [showModelSelector, setShowModelSelector] = React.useState(false);
  const [showAspectRatioSelector, setShowAspectRatioSelector] = React.useState(false);
  const [showResolutionSelector, setShowResolutionSelector] = React.useState(false);
  const [showMaxShotDurationSelector, setShowMaxShotDurationSelector] = React.useState(false);
  const modelSelectorRef = useRef<HTMLDivElement>(null);

  const hasMultipleModels =
    llmModels.length > 1 || llmVlModels.length > 1 ||
    text2imageModels.length > 1 || imageEditModels.length > 1;

  return (
    <>
      {/* 上传区 + Prompt 并排 */}
      <div className="flex items-start gap-3 pt-1">
        <div className="flex flex-col gap-1.5 shrink-0">
          <AttachmentCard
            fileName={audioFileName}
            uploading={uploading}
            disabled={disabled}
            required={true}
            iconType="music"
            label={t('mvGeneration.uploadMusic')}
            tooltip={t('mvGeneration.uploadMusicTooltip')}
            onUploadClick={onUploadClick}
            onClear={onClear}
          />
          {!audioFileName && (
            <button
              type="button"
              onClick={onSelectFromMusic}
              disabled={disabled}
              className="flex items-center justify-center gap-1 w-20 py-1 text-[10px] text-primary bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              <MusicalNoteIcon className="w-3 h-3" />
              {t('mvGeneration.myMusic')}
            </button>
          )}
        </div>
        <textarea
          value={prompt}
          onChange={(e) => onPromptChange(e.target.value)}
          placeholder={t('mvGeneration.promptPlaceholder')}
          className="flex-1 bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none h-24 py-0"
          disabled={disabled}
        />
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 flex-wrap">
          {hasMultipleModels && (
            <div className="relative" ref={modelSelectorRef}>
              <button
                onClick={() => setShowModelSelector(!showModelSelector)}
                disabled={disabled}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
              >
                <CpuChipIcon className="w-3.5 h-3.5 text-green-400" />
                <span>{t('idea.model')}</span>
                <ChevronDownIcon className="w-3 h-3 text-gray-400" />
              </button>
              {showModelSelector && (
                <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] p-4 min-w-[320px]">
                  <div className="space-y-3">
                    {llmModels.length > 1 && (
                      <CompactModelSelector label={t('idea.llmModel')} models={llmModels} value={selectedModels.llmModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmModelId: id })} />
                    )}
                    {llmVlModels.length > 1 && (
                      <CompactModelSelector label={t('idea.visionLlmModel')} models={llmVlModels} value={selectedModels.llmVlModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, llmVlModelId: id })} />
                    )}
                    {text2imageModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageGenModel')} models={text2imageModels} value={selectedModels.text2imageModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, text2imageModelId: id })} />
                    )}
                    {imageEditModels.length > 1 && (
                      <CompactModelSelector label={t('idea.imageEditModel')} models={imageEditModels} value={selectedModels.imageEditModelId}
                        onChange={(id) => onModelsChange({ ...selectedModels, imageEditModelId: id })} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={onOpenSubjectSelector}
            disabled={disabled}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
          >
            <UserIcon className="w-3.5 h-3.5 text-primary" />
            <span>{selectedSubjects.length > 0 ? t('idea.subjectWithCount', { count: selectedSubjects.length }) : t('idea.subject')}</span>
          </button>

          <StyleSelector selectedStyle={selectedStyle} onStyleChange={onStyleChange} disabled={disabled} />

          {/* 单个分镜最大时长 */}
          <div className="relative">
            <button
              onClick={() => setShowMaxShotDurationSelector(!showMaxShotDurationSelector)}
              disabled={disabled}
              className={`flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-[13px] rounded-lg border transition-colors disabled:opacity-50 ${maxShotDuration ? 'text-amber-400 border-amber-400/40' : 'text-gray-300 border-border-subtle'}`}
            >
              <ClockIcon className="w-3.5 h-3.5 text-amber-400" />
              <span>{maxShotDuration ? t('videoGeneration.maxShotDurationSeconds', { value: maxShotDuration }) : t('videoGeneration.maxShotDuration')}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showMaxShotDurationSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[120px]">
                {[null, 2.5, 5, 7.5, 10, 12.5, 15].map((val) => (
                  <button
                    key={val ?? 'no-limit'}
                    onClick={() => { onMaxShotDurationChange(val); setShowMaxShotDurationSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${maxShotDuration === val ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}
                  >
                    {val === null ? t('videoGeneration.maxShotDurationNoLimit') : t('videoGeneration.durationSecondsSuffix', { value: val })}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
              disabled={disabled}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-purple-400" />
              <span>{aspectRatio}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showAspectRatioSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[160px]">
                {Object.entries(aspectRatioLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onAspectRatioChange(value); setShowAspectRatioSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${aspectRatio === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => setShowResolutionSelector(!showResolutionSelector)}
              disabled={disabled}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-bg-surface text-gray-300 text-[13px] rounded-lg border border-border-subtle transition-colors disabled:opacity-50"
            >
              <PhotoIcon className="w-3.5 h-3.5 text-blue-400" />
              <span>{resolutionLabels[resolution]}</span>
              <ChevronDownIcon className="w-3 h-3 text-gray-400" />
            </button>
            {showResolutionSelector && (
              <div className="absolute top-full left-0 mt-2 bg-bg-page border border-border-subtle rounded-lg shadow-lg z-[9999] py-1 min-w-[140px]">
                {Object.entries(resolutionLabels).map(([value, label]) => (
                  <button key={value} onClick={() => { onResolutionChange(value as Resolution); setShowResolutionSelector(false); }}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${resolution === value ? 'text-primary bg-bg-elevated' : 'text-gray-300'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 px-3 py-2 text-[14px] rounded-lg ${hasEnoughCoins ? 'text-gray-400' : 'text-red-400'}`}>
            <CurrencyDollarIcon className="w-4 h-4" />
            <span>20</span>
          </div>
          <button
            onClick={onSubmit}
            disabled={!canSubmit || disabled || !hasEnoughCoins}
            className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-dark hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title={!hasEnoughCoins ? t('mvGeneration.insufficientCoins') : ''}
          >
            <PaperAirplaneIcon className="w-5 h-5 -rotate-45" />
          </button>
        </div>
      </div>

      {selectedSubjects.length > 0 && (
        <div className="mt-3 pt-3 border-t border-border-subtle">
          <div className="flex items-center gap-2 flex-wrap">
            {selectedSubjects.map((asset) => (
              <div key={asset.id} className="flex items-center gap-1 px-2 py-1 bg-bg-elevated rounded-lg text-xs text-gray-300">
                {asset.conceptImageUrl ? (
                  <img src={asset.conceptImageUrl} alt={asset.name} className="w-4 h-4 object-cover rounded" />
                ) : (
                  <UserIcon className="w-4 h-4 text-gray-400" />
                )}
                <span className="truncate max-w-16">{asset.name}</span>
                <button onClick={() => onRemoveSubject(asset.id)} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// ─── 主组件 ───────────────────────────────────────────────────────────────────

export interface MvGenerationTabProps {
  audioFileName: string;
  uploading: boolean;
  audioInputRef: React.RefObject<HTMLInputElement>;
  onAudioUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearAudio: () => void;
  onSelectAudioFromMusic: (name: string, url: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  hasEnoughCoins: boolean;
  prompt: string;
  onPromptChange: (v: string) => void;
  selectedSubjects: IProjectAsset[];
  onOpenSubjectSelector: () => void;
  onRemoveSubject: (id: string) => void;
  selectedStyle: string;
  onStyleChange: (s: string) => void;
  aspectRatio: string;
  onAspectRatioChange: (v: string) => void;
  resolution: Resolution;
  onResolutionChange: (v: Resolution) => void;
  selectedModels: MvSelectedModels;
  onModelsChange: (models: MvSelectedModels) => void;
  maxShotDuration: number | null;
  onMaxShotDurationChange: (v: number | null) => void;
}

export default function MvGenerationTab({
  audioFileName, uploading, audioInputRef,
  onAudioUpload, onClearAudio, onSelectAudioFromMusic, onSubmit, canSubmit,
  hasEnoughCoins, prompt, onPromptChange,
  selectedSubjects, onOpenSubjectSelector, onRemoveSubject,
  selectedStyle, onStyleChange,
  aspectRatio, onAspectRatioChange,
  resolution, onResolutionChange,
  selectedModels, onModelsChange,
  maxShotDuration, onMaxShotDurationChange,
}: MvGenerationTabProps) {
  const { t } = useTranslation(['script', 'common']);
  const [showMusicSelector, setShowMusicSelector] = React.useState(false);

  const handleMusicSelect = (bgm: IBgm) => {
    onSelectAudioFromMusic(bgm.name, bgm.audioUrl || '');
  };

  return (
    <>
      <input
        ref={audioInputRef}
        type="file"
        accept=".mp3,.wav,.m4a,.aac,.flac"
        onChange={onAudioUpload}
        className="hidden"
      />
      <UploadArea
        audioFileName={audioFileName}
        uploading={uploading}
        disabled={false}
        onUploadClick={() => audioInputRef.current?.click()}
        onClear={onClearAudio}
        onSelectFromMusic={() => setShowMusicSelector(true)}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        hasEnoughCoins={hasEnoughCoins}
        prompt={prompt}
        onPromptChange={onPromptChange}
        selectedSubjects={selectedSubjects}
        onOpenSubjectSelector={onOpenSubjectSelector}
        onRemoveSubject={onRemoveSubject}
        selectedStyle={selectedStyle}
        onStyleChange={onStyleChange}
        aspectRatio={aspectRatio}
        onAspectRatioChange={onAspectRatioChange}
        resolution={resolution}
        onResolutionChange={onResolutionChange}
        selectedModels={selectedModels}
        onModelsChange={onModelsChange}
        maxShotDuration={maxShotDuration}
        onMaxShotDurationChange={onMaxShotDurationChange}
      />
      <MusicSelector
        isOpen={showMusicSelector}
        onClose={() => setShowMusicSelector(false)}
        onSelect={handleMusicSelect}
        showLibrary={false}
      />
    </>
  );
}
